'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Package, Plus, Trash2, AlertTriangle, CheckCircle, Upload, FileSpreadsheet, RefreshCw, Search, ChevronDown, ChevronUp, Eraser } from 'lucide-react';
import { getParts, addPart, deletePart, clearAllParts, bulkImportParts, seedDefaultParts, Part } from '@/lib/partsStore';
import EmptyState from '@/components/ui/EmptyState';
import Modal from '@/components/ui/Modal';

const CATEGORIES = [
  'Brakes', 'Engine', 'Filters', 'Electrical', 'Cooling',
  'Suspension', 'Drivetrain', 'Ignition', 'Lubricants', 'Tyres', 'Other',
];

const EMPTY_FORM = {
  name: '',
  partNumber: '',
  category: 'Filters',
  unitPrice: '',
  stock: '0',
  minStock: '5',
};

type SortField = 'partNumber' | 'name' | 'category' | 'unitPrice';
type SortDir = 'asc' | 'desc';

export default function PartsManagementClient() {
  const [parts, setParts] = useState<Part[]>([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  // Seed state
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<{ added: number; skipped: number } | null>(null);

  // Bulk import state
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetRange, setSheetRange] = useState('Sheet1!A1:F200');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ added: number; skipped: number } | null>(null);
  const [importError, setImportError] = useState('');
  const [showImportHelp, setShowImportHelp] = useState(false);

  // Clear confirm
  const [clearModal, setClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Delete confirm
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Search/sort
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('partNumber');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const reload = useCallback(() => setParts(getParts()), []);

  useEffect(() => {
    reload();
  }, [reload]);

  // ── Seed default parts ────────────────────────────────────────────────────
  const handleSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    await new Promise((r) => setTimeout(r, 300));
    const result = seedDefaultParts();
    setSeedResult(result);
    setSeeding(false);
    reload();
  };

  // ── Manual entry ──────────────────────────────────────────────────────────
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setFormError('');
    setFormSuccess('');
  };

  const handleAddPart = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError('Part Name / Description is required.'); return; }
    if (!form.partNumber.trim()) { setFormError('Part Number / SKU is required.'); return; }
    const price = parseFloat(form.unitPrice);
    if (isNaN(price) || price < 0) { setFormError('Unit Price must be a valid number.'); return; }

    // Check duplicate SKU
    const existing = getParts();
    if (existing.some((p) => p.partNumber.toUpperCase() === form.partNumber.trim().toUpperCase())) {
      setFormError(`Part Number "${form.partNumber.trim().toUpperCase()}" already exists.`);
      return;
    }

    setSaving(true);
    await new Promise((r) => setTimeout(r, 400));
    addPart({
      name: form.name.trim(),
      partNumber: form.partNumber.trim().toUpperCase(),
      category: form.category,
      unitPrice: price,
      stock: parseInt(form.stock) || 0,
      minStock: parseInt(form.minStock) || 5,
    });
    setForm(EMPTY_FORM);
    setFormSuccess(`"${form.name.trim()}" added successfully.`);
    setSaving(false);
    reload();
  };

  // ── Google Sheets import ──────────────────────────────────────────────────
  const extractSheetId = (url: string): string | null => {
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  };

  const handleImport = async () => {
    setImportError('');
    setImportResult(null);
    const sheetId = extractSheetId(sheetUrl.trim());
    if (!sheetId) {
      setImportError('Invalid Google Sheets URL. Paste the full URL from your browser address bar.');
      return;
    }
    setImporting(true);
    try {
      const apiUrl = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${encodeURIComponent(sheetRange)}?key=${process.env.NEXT_PUBLIC_GOOGLE_SHEETS_API_KEY || ''}`;
      const res = await fetch(apiUrl);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || `HTTP ${res.status} — make sure the sheet is publicly readable.`);
      }
      const data = await res.json();
      const rows: string[][] = data.values || [];
      if (rows.length < 2) {
        throw new Error('Sheet appears empty or has no data rows. Expected header row + data rows.');
      }

      // Auto-detect columns from header row (case-insensitive)
      const headers = rows[0].map((h: string) => h.trim().toLowerCase());
      const col = (names: string[]) => names.reduce((found, n) => found >= 0 ? found : headers.indexOf(n), -1);

      const nameIdx = col(['part name', 'name', 'description', 'part name/description']);
      const skuIdx = col(['part number', 'sku', 'part number/sku', 'part no', 'partno', 'part#']);
      const catIdx = col(['category', 'cat']);
      const priceIdx = col(['unit price', 'price', 'unitprice', 'cost']);
      const stockIdx = col(['stock', 'qty', 'quantity', 'on hand']);
      const minStockIdx = col(['min stock', 'minimum stock', 'reorder', 'min qty']);

      if (nameIdx < 0 || skuIdx < 0) {
        throw new Error(
          'Could not find required columns. Your sheet must have headers: "Part Name" and "Part Number" (or "SKU"). ' +
          `Found headers: ${rows[0].join(', ')}`
        );
      }

      const incoming = rows.slice(1)
        .filter((r) => r[nameIdx]?.trim() && r[skuIdx]?.trim())
        .map((r) => ({
          name: r[nameIdx]?.trim() || '',
          partNumber: (r[skuIdx]?.trim() || '').toUpperCase(),
          category: catIdx >= 0 ? (r[catIdx]?.trim() || 'Other') : 'Other',
          unitPrice: priceIdx >= 0 ? parseFloat(r[priceIdx]) || 0 : 0,
          stock: stockIdx >= 0 ? parseInt(r[stockIdx]) || 0 : 0,
          minStock: minStockIdx >= 0 ? parseInt(r[minStockIdx]) || 5 : 5,
        }));

      if (incoming.length === 0) {
        throw new Error('No valid data rows found. Check that Part Name and Part Number columns have values.');
      }

      const added = bulkImportParts(incoming);
      setImportResult({ added: added.length, skipped: incoming.length - added.length });
      reload();
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : 'Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  // ── Clear all ─────────────────────────────────────────────────────────────
  const handleClearAll = async () => {
    setClearing(true);
    await new Promise((r) => setTimeout(r, 600));
    clearAllParts();
    setClearing(false);
    setClearModal(false);
    reload();
  };

  // ── Delete single ─────────────────────────────────────────────────────────
  const handleDelete = (id: string) => {
    deletePart(id);
    setDeleteId(null);
    reload();
  };

  // ── Sort / filter ─────────────────────────────────────────────────────────
  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = parts
    .filter((p) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return p.name.toLowerCase().includes(s) || p.partNumber.toLowerCase().includes(s) || p.category.toLowerCase().includes(s);
    })
    .sort((a, b) => {
      let av: string | number = a[sortField];
      let bv: string | number = b[sortField];
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp size={11} className="opacity-20" />;
    return sortDir === 'asc' ? <ChevronUp size={11} className="text-primary" /> : <ChevronDown size={11} className="text-primary" />;
  };

  return (
    <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Parts Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {parts.length} part{parts.length !== 1 ? 's' : ''} in catalogue · Add manually or import from Google Sheets
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary/90 transition-all duration-150 disabled:opacity-60"
          >
            <Package size={15} />
            {seeding ? 'Loading…' : 'Load Default Parts'}
          </button>
          {parts.length > 0 && (
            <button
              onClick={() => setClearModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-danger border border-danger/30 hover:bg-danger/10 transition-all duration-150"
            >
              <Eraser size={15} />
              Clear All Parts
            </button>
          )}
        </div>
      </div>

      {/* Seed result banner */}
      {seedResult && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-success/10 border border-success/20 text-sm text-success">
          <CheckCircle size={16} />
          <span>
            {seedResult.added > 0
              ? `${seedResult.added} part${seedResult.added !== 1 ? 's' : ''} loaded successfully.`
              : 'All default parts are already in the catalogue — nothing new to add.'}
            {seedResult.skipped > 0 && seedResult.added > 0 && ` (${seedResult.skipped} already existed, skipped)`}
          </span>
          <button onClick={() => setSeedResult(null)} className="ml-auto text-success/60 hover:text-success">✕</button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* ── Left: Forms ─────────────────────────────────────────────────── */}
        <div className="xl:col-span-2 space-y-5">

          {/* Manual Entry Form */}
          <div className="card-surface p-5 space-y-4">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Plus size={16} className="text-primary" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Manual Entry</h2>
                <p className="text-xs text-muted-foreground">Add a single part to the catalogue</p>
              </div>
            </div>

            <form onSubmit={handleAddPart} className="space-y-3">
              <div>
                <label className="label-field">Part Name / Description <span className="text-danger">*</span></label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  className="input-field"
                  placeholder="e.g. Front Brake Pads (Set)"
                />
              </div>
              <div>
                <label className="label-field">Part Number / SKU <span className="text-danger">*</span></label>
                <input
                  name="partNumber"
                  value={form.partNumber}
                  onChange={handleFormChange}
                  className="input-field font-mono-data uppercase"
                  placeholder="e.g. BRK-2291"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-field">Category</label>
                  <select name="category" value={form.category} onChange={handleFormChange} className="input-field">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-field">Unit Price (R) <span className="text-danger">*</span></label>
                  <input
                    name="unitPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.unitPrice}
                    onChange={handleFormChange}
                    className="input-field"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-field">Opening Stock</label>
                  <input
                    name="stock"
                    type="number"
                    min="0"
                    value={form.stock}
                    onChange={handleFormChange}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="label-field">Min Stock Level</label>
                  <input
                    name="minStock"
                    type="number"
                    min="0"
                    value={form.minStock}
                    onChange={handleFormChange}
                    className="input-field"
                  />
                </div>
              </div>

              {formError && (
                <p className="text-xs text-danger flex items-center gap-1.5">
                  <AlertTriangle size={12} /> {formError}
                </p>
              )}
              {formSuccess && (
                <p className="text-xs text-positive flex items-center gap-1.5">
                  <CheckCircle size={12} /> {formSuccess}
                </p>
              )}

              <button type="submit" disabled={saving} className="btn-primary w-full disabled:opacity-50">
                {saving ? (
                  <><RefreshCw size={14} className="animate-spin" /> Saving…</>
                ) : (
                  <><Plus size={14} /> Add Part</>
                )}
              </button>
            </form>
          </div>

          {/* Google Sheets Bulk Import */}
          <div className="card-surface p-5 space-y-4">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <FileSpreadsheet size={16} className="text-accent" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Google Sheets Import</h2>
                <p className="text-xs text-muted-foreground">Bulk-load parts from a shared spreadsheet</p>
              </div>
            </div>

            {/* Help toggle */}
            <button
              type="button"
              onClick={() => setShowImportHelp((v) => !v)}
              className="text-xs text-primary flex items-center gap-1 hover:underline"
            >
              {showImportHelp ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              How to set up your Google Sheet
            </button>

            {showImportHelp && (
              <div className="p-3 rounded-lg border border-border text-xs text-muted-foreground space-y-1.5" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                <p className="font-semibold text-foreground">Required sheet columns (header row):</p>
                <p>• <span className="font-mono-data text-primary">Part Name</span> — full description</p>
                <p>• <span className="font-mono-data text-primary">Part Number</span> — unique SKU</p>
                <p>• <span className="font-mono-data text-primary">Category</span> — optional</p>
                <p>• <span className="font-mono-data text-primary">Unit Price</span> — numeric, optional</p>
                <p>• <span className="font-mono-data text-primary">Stock</span> — opening qty, optional</p>
                <p>• <span className="font-mono-data text-primary">Min Stock</span> — reorder level, optional</p>
                <p className="mt-2 font-semibold text-foreground">Sheet must be publicly readable:</p>
                <p>Share → Anyone with the link → Viewer</p>
                <p className="mt-2 font-semibold text-foreground">API Key required:</p>
                <p>Add <span className="font-mono-data">NEXT_PUBLIC_GOOGLE_SHEETS_API_KEY</span> to your .env file.</p>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="label-field">Google Sheets URL</label>
                <input
                  value={sheetUrl}
                  onChange={(e) => { setSheetUrl(e.target.value); setImportError(''); setImportResult(null); }}
                  className="input-field text-xs"
                  placeholder="https://docs.google.com/spreadsheets/d/…"
                />
              </div>
              <div>
                <label className="label-field">Range (A1 notation)</label>
                <input
                  value={sheetRange}
                  onChange={(e) => setSheetRange(e.target.value)}
                  className="input-field font-mono-data text-xs"
                  placeholder="Sheet1!A1:F200"
                />
              </div>

              {importError && (
                <div className="p-3 rounded-lg border border-danger/30 bg-danger/5 text-xs text-danger flex items-start gap-2">
                  <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                  <span>{importError}</span>
                </div>
              )}

              {importResult && (
                <div className="p-3 rounded-lg border border-positive/30 bg-positive/5 text-xs text-positive flex items-start gap-2">
                  <CheckCircle size={13} className="flex-shrink-0 mt-0.5" />
                  <span>
                    Import complete — <strong>{importResult.added}</strong> part{importResult.added !== 1 ? 's' : ''} added
                    {importResult.skipped > 0 && `, ${importResult.skipped} skipped (duplicate SKU)`}.
                  </span>
                </div>
              )}

              <button
                type="button"
                onClick={handleImport}
                disabled={importing || !sheetUrl.trim()}
                className="btn-secondary w-full disabled:opacity-50"
              >
                {importing ? (
                  <><RefreshCw size={14} className="animate-spin" /> Importing…</>
                ) : (
                  <><Upload size={14} /> Import from Google Sheets</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── Right: Parts Table ───────────────────────────────────────────── */}
        <div className="xl:col-span-3">
          <div className="card-surface">
            {/* Toolbar */}
            <div className="px-5 py-4 border-b border-border">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input-field pl-9 text-sm"
                  placeholder="Search parts…"
                />
              </div>
            </div>

            {filtered.length === 0 ? (
              <EmptyState
                icon={Package}
                heading={parts.length === 0 ? 'No parts yet' : 'No results'}
                description={
                  parts.length === 0
                    ? 'Add your first part using the manual form or import from Google Sheets.'
                    : 'No parts match your search.'
                }
                action={search ? { label: 'Clear Search', onClick: () => setSearch('') } : undefined}
              />
            ) : (
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                      {(
                        [
                          { key: 'partNumber' as SortField, label: 'SKU' },
                          { key: 'name' as SortField, label: 'Part Name' },
                          { key: 'category' as SortField, label: 'Category' },
                          { key: 'unitPrice' as SortField, label: 'Unit Price' },
                        ] as { key: SortField; label: string }[]
                      ).map((col) => (
                        <th key={col.key} className="px-4 py-3 text-left">
                          <button
                            onClick={() => handleSort(col.key)}
                            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {col.label}
                            <SortIcon field={col.key} />
                          </button>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground">Stock</th>
                      <th className="px-4 py-3 w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map((part) => {
                      const lowStock = part.stock < part.minStock;
                      return (
                        <tr key={part.id} className="hover:bg-secondary/40 transition-colors duration-100 group">
                          <td className="px-4 py-3 font-mono-data text-xs text-muted-foreground">{part.partNumber}</td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-foreground">{part.name}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-muted-foreground border border-border">
                              {part.category}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono-data text-sm text-foreground">
                            R {part.unitPrice.toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-semibold ${lowStock ? 'text-warning' : 'text-positive'}`}>
                              {part.stock}
                              {lowStock && <AlertTriangle size={10} className="inline ml-1" />}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setDeleteId(part.id)}
                              className="p-1.5 rounded-md text-muted-foreground hover:bg-danger/10 hover:text-danger transition-all duration-150 opacity-0 group-hover:opacity-100"
                              title="Remove part"
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {filtered.length > 0 && (
              <div className="px-5 py-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Showing {filtered.length} of {parts.length} part{parts.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clear All Modal */}
      <Modal isOpen={clearModal} onClose={() => setClearModal(false)} title="Clear All Parts">
        <div className="space-y-4">
          <div className="p-3 rounded-lg border border-warning/30 bg-warning/5 flex items-start gap-2">
            <AlertTriangle size={16} className="text-warning flex-shrink-0 mt-0.5" />
            <div className="text-sm text-foreground">
              <p className="font-semibold">This will permanently delete all {parts.length} parts from the catalogue.</p>
              <p className="text-muted-foreground mt-1 text-xs">
                Historic job cards that reference old part numbers will retain their recorded data — only the live catalogue is cleared.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setClearModal(false)} className="btn-secondary flex-1">Cancel</button>
            <button
              onClick={handleClearAll}
              disabled={clearing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-danger text-white hover:bg-danger/90 disabled:opacity-50 transition-all"
            >
              {clearing ? <><RefreshCw size={13} className="animate-spin" /> Clearing…</> : <><Eraser size={13} /> Clear All Parts</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Single Modal */}
      <Modal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Remove Part"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Remove <strong className="text-foreground">{parts.find((p) => p.id === deleteId)?.name}</strong> from the catalogue?
            This does not affect historic job cards.
          </p>
          <div className="flex gap-3">
            <button onClick={() => setDeleteId(null)} className="btn-secondary flex-1">Cancel</button>
            <button
              onClick={() => deleteId && handleDelete(deleteId)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-danger text-white hover:bg-danger/90 transition-all"
            >
              <Trash2 size={13} /> Remove
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
